import React from 'react';
import ImageRadioButton from './ImageRadioButton';

/**
 * ImageRadioButtonGroupContainer component
 *
 * A functional component that renders a group of image-based radio buttons.
 * It can either function as a single select (radio buttons) or multi-select (checkboxes).
 *
 * @param {Array} options - An array of objects representing the options. Each option should have `value`, `label`, and `image` properties.
 * @param {Array|string} selectedValue - The currently selected value(s) from the image radio buttons.
 * @param {string} label - The label for the image radio button group.
 * @param {boolean} multiSelect - Determines whether the component allows multi-select (checkboxes).
 * @param {boolean} isDarkMode - Determines whether the component should render in dark mode.
 * @param {function} onChange - A function that gets called when a radio button is selected, passing the selected value(s).
 * @param {string} type -  The type select items icon e.g. default se;ect ('normal'), add Option select ('add') or remove option select ('remove').
 * 
 * @returns {JSX.Element} - The rendered group of image-based radio buttons.
 */
const ImageRadioButtonGroup = ({
  options,
  selectedValue,
  label,
  multiSelect = false,
  isDarkMode = false,
  onChange,
  type = 'normal',
  disabled = false
}) => {
  const handleChange = (value) => {
    if (disabled) return

    if (multiSelect) {
      // Ensure selectedValue is an array for multi-select
      const currentSelection = Array.isArray(selectedValue) ? selectedValue : [];
      // Toggle the selected value in case of multi-select
      const newSelectedValues = currentSelection.includes(value)
        ? currentSelection.filter((v) => v !== value) // Deselect if already selected
        : [...currentSelection, value]; // Add to selected list
      onChange(newSelectedValues);
    } else {
      // Single select logic
      onChange(value);
    }
  };

  return (
    <div className={
      `image-radio-buttons-group-wrapper
      ${disabled && 'image-radio-buttons-group-wrapper--is-disabled'}
      `}
    >
      {label && <label className="image-radio-buttons-group-label">{label}</label>}
      <div className="image-radio-buttons-group">
        {options.map((option, index) => (
          <ImageRadioButton
            key={index}
            value={option.value}
            label={option.label}
            image={option.image}
            onHoverImage={option?.hover_image}
            selectedValue={multiSelect ? selectedValue : selectedValue}
            onChange={() => handleChange(option.value)}
            multiSelect={multiSelect}
            type={type}
            isDisabled={disabled}
          />
        ))}
      </div>
    </div>
  );
};

export default ImageRadioButtonGroup;
